import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

import { markyWebsiteUseCases } from '@/constants/markyWebsiteUseCases';

const FEATURED_TYPES = markyWebsiteUseCases.slice(0, 4);

const MarkyWebsiteBuilderSection = () => {
  const { t } = useLocale('marky');

  return (
    <section
      aria-labelledby='marky-website-builder-title'
      className='relative isolate overflow-hidden bg-[#1b1322] py-20 text-[#fffaf3] sm:py-24 lg:py-28'
    >
      <div
        aria-hidden='true'
        className='absolute -left-28 top-24 h-64 w-64 rounded-full border-[40px] border-[#8b5cf6]/20'
      />
      <div
        aria-hidden='true'
        className='absolute -right-24 bottom-10 h-72 w-72 rotate-12 border border-[#f4b860]/20'
      />

      <div className='relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20 lg:px-8'>
        <div>
          <p className='text-sm font-bold uppercase tracking-[0.2em] text-[#cbb5ff]'>
            {t('Web development')}
          </p>
          <h2
            id='marky-website-builder-title'
            className='mt-4 max-w-xl text-4xl font-black tracking-[-0.035em] text-[#fffaf3] sm:text-5xl lg:text-6xl'
          >
            {t('Build & deploy websites from chat')}
          </h2>
          <p className='mt-6 max-w-xl text-lg leading-8 text-[#d8cfdc]'>
            {t(
              'Describe the site you need. Marky scaffolds it, wires up a database, deploys it to a live URL — and iterates with you in plain language.',
            )}
          </p>

          <ul className='mt-8 space-y-4'>
            {[
              'Live preview while it codes',
              'Databases, auth and custom domains',
              'Checkpoints you can roll back to',
            ].map((item) => (
              <li key={item} className='flex items-start gap-3 text-[#f5eef7]'>
                <CheckCircleIcon
                  className='mt-0.5 h-5 w-5 shrink-0 text-[#b7dd55]'
                  aria-hidden='true'
                />
                <span>{t(item)}</span>
              </li>
            ))}
          </ul>

          <Link
            href='/marky/website-builder'
            className='mt-9 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#fffaf3] px-6 py-3 font-bold text-[#24162d] transition-colors hover:bg-[#e8dcff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#cbb5ff]'
          >
            {t('See what it can do')}
            <ArrowRightIcon className='h-4 w-4' aria-hidden='true' />
          </Link>
        </div>

        <div className='grid grid-cols-2 gap-3 sm:gap-4'>
          {FEATURED_TYPES.map((useCase, index) => (
            <Link
              key={useCase.slug}
              href={`/marky/website-builder/${useCase.slug}`}
              className={`group overflow-hidden rounded-2xl border border-white/15 bg-[#2a1e31] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#cbb5ff] ${
                index === 0
                  ? 'col-span-2 sm:mr-12'
                  : index === 3
                    ? 'col-span-2 sm:ml-12'
                    : ''
              }`}
            >
              <div className='relative aspect-[3/2] overflow-hidden'>
                <Image
                  src={useCase.image}
                  alt={t(useCase.shortName, null, {
                    default: useCase.imageAlt,
                  })}
                  fill
                  sizes={
                    index === 0 || index === 3
                      ? '(min-width: 1024px) 45vw, 90vw'
                      : '(min-width: 1024px) 22vw, 45vw'
                  }
                  className='object-cover transition-transform duration-500 motion-reduce:transition-none group-hover:scale-[1.025]'
                />
              </div>
              <span className='flex min-h-[54px] items-center justify-between gap-3 px-4 py-3 font-bold text-[#fffaf3]'>
                {t(useCase.shortName, null, { default: useCase.shortName })}
                <span
                  aria-hidden='true'
                  className='text-[#cbb5ff] transition-transform group-hover:translate-x-1 motion-reduce:transition-none'
                >
                  →
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MarkyWebsiteBuilderSection;
